import { useRef } from 'react';
import { ArrowDownTrayIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useClickOutsideBlock } from '../../helpers/useClickOutside';
import { buildAllGovernanceDownloadText, buildGovernanceDownloadText, getViewGovernance } from '../../consts/viewGovernance';

function sanitizeFilePart(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'demand-planning-governance';
}

function downloadTextFile(filenameRoot, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilePart(filenameRoot)}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ViewGovernanceDialog({
  open,
  onOpenChange,
  viewId,
  viewName,
  marginFromSidebar,
}) {
  const modalRef = useRef(null);
  const governance = getViewGovernance(viewId);

  useClickOutsideBlock(modalRef, () => onOpenChange?.(false));

  if (!open || !viewId) return null;

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto overflow-x-hidden p-4"
    >
      <div className="fixed inset-0 z-[60] bg-gray-700 opacity-30" />
      <div
        ref={modalRef}
        style={{
          '--content-width': marginFromSidebar ? `calc(980px - ${marginFromSidebar}px)` : '980px',
          marginLeft: marginFromSidebar ? `${marginFromSidebar}px` : '0',
        }}
        className="relative z-[70] flex h-[min(90vh,920px)] w-full max-w-[min(96vw,var(--content-width))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
      >
        <header className="shrink-0 border-b border-gray-200 px-5 py-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
              <InformationCircleIcon className="h-3.5 w-3.5" aria-hidden />
              Governance
            </span>
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close governance dialog"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <h2 className="text-left text-lg font-semibold tracking-[-0.02em] text-gray-900">
            {viewName} governance
          </h2>
          <p className="mt-1 max-w-3xl text-left text-sm leading-6 text-gray-500">
            {governance.summary}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="grid gap-3 md:grid-cols-3">
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">Purpose</p>
              <p className="mt-2 text-sm leading-6 text-gray-900">{governance.purpose}</p>
            </section>
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">Source of truth</p>
              <p className="mt-2 text-sm leading-6 text-gray-900">{governance.sourceOfTruth}</p>
            </section>
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">Not for</p>
              <p className="mt-2 text-sm leading-6 text-gray-900">{governance.notFor}</p>
            </section>
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.9fr)]">
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">Quick governance summary</h3>
              <div className="mt-3 space-y-3">
                {governance.keyRules.map(rule => (
                  <div key={rule} className="rounded-md border border-gray-200 bg-white px-3 py-3">
                    <p className="text-sm leading-6 text-gray-900">{rule}</p>
                  </div>
                ))}
              </div>
            </section>

            <aside className="space-y-4">
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900">Downstream impact</h3>
                <div className="mt-3 space-y-2">
                  {governance.downstreamImpact.map(item => (
                    <div key={item} className="rounded-md border border-gray-200 bg-white px-3 py-3">
                      <p className="text-sm leading-6 text-gray-900">{item}</p>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>

          <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">Full governance guidance</h3>
            <div className="mt-3 space-y-3">
              {governance.fullGuidance.map(item => (
                <div key={item} className="rounded-md border border-gray-200 bg-white px-3 py-3">
                  <p className="text-sm leading-6 text-gray-900">{item}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <footer className="flex shrink-0 justify-center border-t border-gray-200 bg-white px-5 py-4">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
            onClick={() => downloadTextFile(
              viewId === 'cogs-canonical-view' ? 'cogs-canonical-view-governance' : 'demand-planning-governance',
              viewId === 'cogs-canonical-view'
                ? buildGovernanceDownloadText(governance)
                : buildAllGovernanceDownloadText(),
            )}
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
            Download full governance
          </button>
        </footer>
      </div>
    </div>
  );
}
